import heic2any from "heic2any";
import {
  face_fd_roi_padding,
  face_fd_roi_ratio,
  face_fd_roi_visualization_shrink_frac,
  liveness_threshold,
} from "./constants";
import { bbox } from "./types";

export const bboxIsInsideRoi = (bbox: bbox, ROI: bbox) => {
  return (
    bbox.x >= ROI.x &&
    bbox.y >= ROI.y &&
    bbox.x + bbox.width <= ROI.x + ROI.width &&
    bbox.y + bbox.height <= ROI.y + ROI.height
  );
};

export const checkMobile = () => {
  let check = false;
  (function (a) {
    if (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
        a
      ) ||
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
        a.substr(0, 4)
      )
    )
      check = true;
  })(navigator.userAgent || navigator.vendor || (window as any).opera);
  return check;
};

export const calculateRoiPosition = (
  videoWidth: number,
  videoHeight: number,
  shrinkCoef: number = 0
) => {
  const w =
    videoWidth /
    ((1 + 2 * (face_fd_roi_padding + shrinkCoef)) * face_fd_roi_ratio);
  const h = videoHeight / (1 + 2 * (face_fd_roi_padding + shrinkCoef));
  const height = Math.min(w, h);
  const width = height * face_fd_roi_ratio;
  const x = videoWidth / 2 - width / 2;
  const y = videoHeight / 2 - height / 2;
  return [x, y, width, height];
};

export const downsizeImg = (imgWidth: number, imgHeight: number) => {
  const threshold = 1000;
  const maxSide = Math.max(imgWidth, imgHeight);
  let downSizedWidth = imgWidth;
  let downSizedHeight = imgHeight;
  if (maxSide > threshold) {
    const imgRatio = imgWidth / imgHeight;
    downSizedWidth = threshold;
    downSizedHeight = downSizedWidth / imgRatio;
  }
  return { downSizedWidth, downSizedHeight };
};

export const drawROI = (
  canvas: HTMLCanvasElement,
  videoWidth: number,
  videoHeight: number
) => {
  canvas.width = videoWidth;
  canvas.height = videoHeight;
  const ctx = canvas.getContext("2d")!;
  ctx.strokeStyle = "blue";
  ctx.lineWidth = 10;
  const [x, y, w, h] = calculateRoiPosition(
    videoWidth,
    videoHeight,
    face_fd_roi_visualization_shrink_frac
  );
  ctx.strokeRect(x, y, w, h);
};

export const isImageValid = (
  facesDetected: number,
  bboxInsideRoi: boolean,
  personImageIsSet: boolean,
  liveness?: number
) => {
  if (facesDetected === 0) {
    return "На фото не найдено лиц! Повторите попытку";
  }
  if (facesDetected > 1) {
    return "На фото найдено больше одно лица! Повторите попытку";
  }
  if (liveness) {
    if (liveness < liveness_threshold) {
      return "Не найдено живых лиц!";
    }
  }
  if (!bboxInsideRoi && !personImageIsSet) {
    return "Лицо находится вне рамки. Повторите попытку";
  }
};

export const heicToURL = async (file: File) => {
  // get image as blob url
  let blobURL = URL.createObjectURL(file);
  // convert "fetch" the new blob url
  let blobRes = await fetch(blobURL);
  // convert response to blob
  let blob = await blobRes.blob();
  // convert to PNG - response is blob
  let conversionResult = await heic2any({ blob });
  return URL.createObjectURL(conversionResult as Blob);
};
